<template>
  <v-card :class="$vuetify.dark ? 'card-dark ' : 'card-light '">
    <v-card-title>
      <span class="body-1 beez-bold pl-3">Execution History </span>
      <v-spacer />
      <v-spacer />
      <v-btn class="text-capitalize" flat outline @click.stop="showId = !showId"
        >{{ showId ? 'Hide Execution ID' : 'Show Execution ID' }}
      </v-btn>
      <v-tooltip bottom>
        <v-btn slot="activator" flat icon @click="getData()">
          <v-icon>cached</v-icon>
        </v-btn>
        <span>Refresh</span>
      </v-tooltip>
    </v-card-title>
    <v-card-text>
      <v-layout row wrap>
        <v-flex md6 xs12>
          <v-text-field
            v-model="search"
            clearable
            color="secondary"
            label="Search"
            placeholder="Search by Execution ID or Bot/Workflow ID, Name or Description"
            prepend-icon="search"
            @change="searchExecutions"
          >
          </v-text-field>
        </v-flex>
        <v-flex md3 xs12>
          <v-select
            v-model="searchStatus"
            :items="executionStatus"
            class="mx-2"
            item-text="text"
            item-value="value"
            label="Execution Status"
            multiple
            small-chips
            @change="searchExecutions"
          >
            <template v-slot:selection="{ item, index }">
              <v-chip v-if="index === 0">
                <span>{{ item.text }}</span>
              </v-chip>
              <span v-if="index === 1" class="grey--text caption"
                >(+{{ searchStatus.length - 1 }} others)</span
              >
            </template>
          </v-select>
        </v-flex>
        <v-flex md3 xs12>
          <v-text-field
            v-model="searchStartedBy"
            clearable
            label="Execution Started By"
            placeholder="User name (Email Id in an Email address) ex: 'name' for name@domain.com"
            @change="searchExecutions"
          ></v-text-field>
        </v-flex>
      </v-layout>
      <v-expand-transition>
        <v-layout row wrap>
          <v-flex md6 xs12>
            <v-menu
              v-model="fromDateMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              full-width
              lazy
              min-width="290px"
              offset-y
              transition="scale-transition"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-on="on"
                  :value="computedFromDate"
                  label="Executions started from"
                  prepend-icon="event"
                  readonly
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="fromDate"
                :max="toDate"
                @input="fromDateMenu = false"
                @change="searchExecutions"
              ></v-date-picker>
            </v-menu>
          </v-flex>
          <v-flex md6 xs12>
            <v-menu
              v-model="toDateMenu"
              :close-on-content-click="false"
              :nudge-right="40"
              full-width
              lazy
              min-width="290px"
              offset-y
              transition="scale-transition"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-on="on"
                  :value="computedToDate"
                  label="Executions started till"
                  prepend-icon="event"
                  readonly
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="toDate"
                :min="fromDate"
                @input="toDateMenu = false"
                @change="searchExecutions"
              ></v-date-picker>
            </v-menu>
          </v-flex>
        </v-layout>
      </v-expand-transition>
      <v-data-table
        key="v-data-table-executions-main"
        :headers="showId ? headersWithID : headers"
        :items="executions"
        :loading="executionLoading"
        :pagination.sync="pagination"
        :rows-per-page-items="headerPageItems"
        :total-items="pagination.totalItems"
        item-key="hexId"
      >
        <v-progress-linear slot="progress" color="accent" indeterminate />
        <template slot="items" slot-scope="props">
          <tr>
            <td max-width="5px">
              <v-menu :nudge-width="200" offset-x>
                <template v-slot:activator="{ on }">
                  <v-btn :loading="props.item.retriggerLoader" icon v-on="on">
                    <v-icon>more_vert</v-icon>
                  </v-btn>
                </template>
                <v-list two-line>
                  <v-list-tile
                    :disabled="props.item.runStatus !== 'FAILED'"
                    @click="reTrigger(props.item)"
                  >
                    <v-list-tile-content>
                      <v-list-tile-title>
                        Re-trigger
                      </v-list-tile-title>
                      <v-list-tile-sub-title>
                        Re-trigger the execution
                      </v-list-tile-sub-title>
                    </v-list-tile-content>
                  </v-list-tile>
                  <v-list-tile
                    v-if="
                      props.item.executionType === 'WORKFLOW' &&
                        can('EXECUTION_LINK')
                    "
                    :to="{
                      name: 'workflow-detail',
                      params: { hexId: props.item.workflow }
                    }"
                  >
                    <v-list-tile-content>
                      <v-list-tile-title>
                        Goto Workflow
                      </v-list-tile-title>
                      <v-list-tile-sub-title>
                        Navigate to the Workflow
                      </v-list-tile-sub-title>
                    </v-list-tile-content>
                  </v-list-tile>
                  <v-list-tile
                    v-if="
                      props.item.executionType === 'BOT' &&
                        can('EXECUTION_LINK')
                    "
                    :to="{
                      name: 'bot-detail',
                      params: { hexId: props.item.bot }
                    }"
                  >
                    <v-list-tile-content>
                      <v-list-tile-title>
                        Goto Bot
                      </v-list-tile-title>
                      <v-list-tile-sub-title>
                        Navigate to the Bot
                      </v-list-tile-sub-title>
                    </v-list-tile-content>
                  </v-list-tile>
                  <v-list-tile @click="focusCurrentExecution(props.item)">
                    <v-list-tile-content>
                      <v-list-tile-title>
                        Focus
                      </v-list-tile-title>
                      <v-list-tile-sub-title>
                        Focus on current execution
                      </v-list-tile-sub-title>
                    </v-list-tile-content>
                  </v-list-tile>
                  <v-list-tile
                    :to="{
                      name: 'task-report',
                      query: { executionId: props.item.hexId }
                    }"
                  >
                    <v-list-tile-content>
                      <v-list-tile-title>
                        Workflow Tasks
                      </v-list-tile-title>
                      <v-list-tile-sub-title>
                        Navigate to the report of the workflow tasks
                      </v-list-tile-sub-title>
                    </v-list-tile-content>
                  </v-list-tile>
                </v-list>
              </v-menu>
            </td>
            <td class="text-xs-left" v-if="showId">
              <div>
                <div class="font-weight-bold">
                  {{ props.item.hexId }}
                </div>
                <br />
                <div>
                  {{ props.item.externalId }}
                </div>
              </div>
            </td>
            <td class="text-xs-left" @click="props.expanded = !props.expanded">
              <div class="body-1 font-weight-bold" style="max-width: 50px">
                {{ format(props.item.executionType) }}
              </div>
              <div class="caption" style="max-width: 50px">
                {{ format(props.item.executedAs) }}
              </div>
            </td>
            <td class="text-xs-left" @click="props.expanded = !props.expanded">
              <div class="body-1 font-weight-bold">
                {{
                  props.item.executionType === 'BOT'
                    ? props.item.botName
                    : props.item.workflowName
                }}
              </div>
              <v-tooltip bottom>
                <div slot="activator" class="caption text-truncate">
                  {{
                    props.item.executionType === 'BOT'
                      ? props.item.botDescription
                      : props.item.workflowDescription
                  }}
                </div>
                <span>{{
                  props.item.executionType === 'BOT'
                    ? props.item.botDescription
                    : props.item.workflowDescription
                }}</span>
              </v-tooltip>
            </td>
            <td class="text-xs-left" @click="props.expanded = !props.expanded">
              <div
                :class="
                  props.item.runStatus === 'FAILED'
                    ? 'red--text'
                    : props.item.runStatus === 'OBSOLETE'
                    ? 'grey--text'
                    : 'green--text'
                "
              >
                <div class="body-1 font-weight-bold">
                  {{ format(props.item.runStatus) }}
                </div>
              </div>
            </td>
            <td class="text-xs-left" @click="props.expanded = !props.expanded">
              <div class="body-1 font-weight-bold">
                {{ props.item.requestedByName }}
              </div>
            </td>
            <td class="text-xs-left" @click="props.expanded = !props.expanded">
              <div class="caption">
                {{ getLocalTime(props.item.createdAt) }}
              </div>
            </td>
            <td class="text-xs-left" @click="props.expanded = !props.expanded">
              <div v-if="props.item.completedAt">
                {{ getLocalTime(props.item.completedAt) }}
              </div>
            </td>
          </tr>
        </template>
        <template slot="expand" slot-scope="props">
          <execution-detail :execution="props.item" :show-id="showId" />
        </template>
        <template slot="no-data">
          <v-alert :value="true" color="warning" icon="warning">
            No Executions found!
          </v-alert>
        </template>
        <template slot="no-results">
          <v-alert :value="true" color="warning" icon="warning">
            Your search for "{{ search }}" not found.
          </v-alert>
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
/* eslint-disable indent */
import ExecutionDetail from '@/components/execution/ExecutionDetail.vue'
import { mapGetters, mapState } from 'vuex'
import moment from 'moment'
import _ from 'lodash'
import axios from 'axios'

export default {
  name: 'Execution',
  components: {
    ExecutionDetail
  },
  data: () => ({
    executions: [],
    urlFromForm: '',
    isUrlSetFromFilter: false,
    executionLoading: false,
    showId: false,
    title: 'Executions',
    fromDateMenu: false,
    fromDate: moment(new Date())
      .subtract(1, 'M')
      .toISOString()
      .substr(0, 10),
    toDateMenu: false,
    toDate: moment()
      .add(1, 'd')
      .toDate()
      .toISOString()
      .substr(0, 10),
    rowsPerPage: 0,
    descending: false,
    sortBy: '',
    pagination: {
      sortBy: 'createdAt',
      rowsPerPage: 10,
      descending: true,
      totalItems: 0,
      page: 1
    },
    currentPage: 0,
    paginationItem: {
      sortBy: 'createdAt'
    },
    search: '',
    headerPageItems: [5, 10, 25],
    searchStatus: [
      'QUEUED',
      'RUNNING',
      'SUCCESSFUL',
      'FAILED',
      'OBSOLETE',
      'RECEIVED',
      'AWAITING_USER_INPUTS'
    ],
    executionStatus: [
      { text: 'Queued', value: 'QUEUED' },
      { text: 'Running', value: 'RUNNING' },
      { text: 'Successful', value: 'SUCCESSFUL' },
      { text: 'Failed', value: 'FAILED' },
      { text: 'Obsolete', value: 'OBSOLETE' },
      { text: 'Received', value: 'RECEIVED' },
      { text: 'Awaiting user inputs', value: 'AWAITING_USER_INPUTS' }
    ],
    searchStartedBy: '',
    userRules: [
      v =>
        (v && _.isArray(v.toString().match('^[a-zA-Z0-9]+$'))) ||
        'Does not look like a valid name!'
    ],
    headers: [
      {
        text: 'Actions',
        align: 'left',
        width: '5px',
        sortable: false
      },
      { text: 'Execution Type / Mode', value: 'executionType' },
      { text: 'Name / Description', value: 'name' },
      { text: 'Run Status', value: 'runStatus' },
      { text: 'Started By', value: 'requestedBy' },
      { text: 'Started At', value: 'createdAt' },
      { text: 'Completed At', value: 'completedAt' }
    ],
    headersWithID: [
      {
        text: 'Actions',
        align: 'left',
        width: '5px',
        sortable: false
      },
      {
        text: 'Execution ID / External ID',
        align: 'left',
        value: 'hexId',
        sortable: false
      },
      { text: 'Execution Type / Mode', value: 'executionType' },
      { text: 'Name / Description', value: 'name' },
      { text: 'Run Status', value: 'runStatus' },
      { text: 'Started By', value: 'requestedBy' },
      { text: 'Started At', value: 'createdAt' },
      { text: 'Completed At', value: 'completedAt' }
    ]
  }),

  watch: {
    pagination: {
      handler() {
        try {
          if (
            this.pagination.page !== this.currentPage ||
            this.pagination.rowsPerPage !== this.rowsPerPage ||
            this.pagination.sortBy !== this.sortBy ||
            this.pagination.descending !== this.descending
          ) {
            this.currentPage = this.pagination.page
            this.rowsPerPage = this.pagination.rowsPerPage
            this.sortBy = this.pagination.sortBy
            this.descending = this.pagination.descending
            this.getData()
          }
          // eslint-disable-next-line no-empty
        } catch (error) {}
      },
      deep: true
    }
  },

  async mounted() {
    this.setFilter()
    await this.getData()
  },

  computed: {
    ...mapGetters({
      can: 'can'
    }),
    computedFromDate() {
      return this.fromDate
        ? moment(this.fromDate).format('dddd, MMMM Do YYYY')
        : ''
    },
    computedToDate() {
      return this.toDate ? moment(this.toDate).format('dddd, MMMM Do YYYY') : ''
    }
  },

  methods: {
    async getData() {
      await this.refreshFilter()
      await this.fetchExecutions(this.urlFromForm)
      if (this.isUrlSetFromFilter) {
        //base64 the urlParams
        let condenseURL = btoa(this.urlFromForm)
        //push the filter to url
        await this.$router.push({ query: { filter: condenseURL } }).catch()
      }
    },
    focusCurrentExecution(item) {
      this.$router
        .push({ name: 'execution', params: { hexId: item.hexId } })
        .catch()
    },
    searchExecutions() {
      this.getData()
    },
    clearSearch() {
      this.search = ''
      this.getData()
    },
    setFilter() {
      try {
        let filter = this.$route.query.filter
        //convert filter from base64 to json
        if (!filter) {
          this.isUrlSetFromFilter = true
          return
        }
        let urlParams = atob(filter.toString())
        let params = new URLSearchParams(urlParams)
        this.pagination.page = parseInt(params.get('page'))
        this.pagination.rowsPerPage = parseInt(params.get('size'))
        this.pagination.sortBy = params.get('sort').split(',')
          ? params.get('sort').split(',')[0]
          : params.get('sort')
        this.pagination.descending = params.get('sort').split(',')
          ? params.get('sort').split(',')[1] === 'desc'
          : false
        this.search = params.get('search')
        // convert to YYYY-MM-DD format
        let fromDate = params.get('fromDate')
        if (fromDate) {
          this.fromDate = moment(fromDate).format('YYYY-MM-DD')
        }
        let toDate = params.get('toDate')
        if (toDate) {
          this.toDate = moment(toDate).format('YYYY-MM-DD')
        }
        this.searchStartedBy = params.get('startedBy')
        // covert comma separated string to array
        let searchStatus = params.get('status')
        if (searchStatus) {
          this.searchStatus = searchStatus.split(',')
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log(e)
      }
      this.isUrlSetFromFilter = true
    },
    async refreshFilter() {
      this.filterItems = []
      let urlParams = ''
      urlParams = `page=${this.pagination.page}`
      if (this.pagination.rowsPerPage !== -1) {
        urlParams =
          urlParams +
          `&size=${this.pagination.rowsPerPage}&sort=${this.pagination.sortBy}${
            this.pagination.descending ? ',desc' : ''
          }`
      }
      if (
        this.search !== undefined &&
        this.search !== null &&
        this.search !== ''
      ) {
        urlParams = urlParams + `&search=${this.search}`
      }
      if (
        this.fromDate !== undefined &&
        this.fromDate !== null &&
        this.fromDate !== '' &&
        this.toDate !== undefined &&
        this.toDate !== null &&
        this.toDate !== ''
      ) {
        urlParams =
          urlParams +
          `&fromDate=${moment(
            this.fromDate,
            'YYYY-MM-DD'
          ).toISOString()}&toDate=${moment(
            this.toDate,
            'YYYY-MM-DD'
          ).toISOString()}`
      }
      if (
        this.searchStatus !== undefined &&
        this.searchStatus !== null &&
        this.searchStatus.length > 0
      ) {
        urlParams = urlParams + `&status=${this.searchStatus.toString()}`
      }
      if (
        this.searchStartedBy !== undefined &&
        this.searchStartedBy !== null &&
        this.searchStartedBy !== ''
      ) {
        urlParams = urlParams + `&startedBy=${this.searchStartedBy}`
      }
      this.urlFromForm = urlParams
    },

    async fetchExecutions(urlParams) {
      this.executionLoading = true
      let url = '/api/v1/executions/standalone?projection=withReferenceHexId'
      if (urlParams !== undefined && urlParams !== null) {
        url = url + '&' + urlParams
      }
      axios
        .get(url)
        .then(response => {
          this.pagination.totalItems = response.data.page.totalElements
          this.executions = response.data._embedded.executions.map(item => {
            item.retriggerLoader = false
            return item
          })
          this.executionLoading = false
        })
        .catch(error => {
          console.log(error)
          this.executionLoading = false
        })
    },

    format(input) {
      return _.capitalize(_.startCase(input))
    },
    getLocalTime(input) {
      return moment(input)
        .utc()
        .local()
        .format('DD MMM YYYY, hh:mm:ss a')
    },

    async reTrigger(item) {
      item.retriggerLoader = true
      if (item.runStatus !== 'FAILED') {
        await this.$store.dispatch('triggerVueNotification', {
          message: 'Only Failed executions can be Re-triggered',
          type: 'error'
        })
      } else if (item.executionType === 'WORKFLOW') {
        await this.$store.dispatch('retriggerWorkflow', item.hexId)
      } else if (item.executionType === 'BOT') {
        await this.$store.dispatch('retriggerBot', item.hexId)
      } else {
        await this.$store.dispatch('directNotification', {
          type: 'error',
          message: 'Unknown Execution Type, can only re-trigger Bot/Workflow Executions',
          description: 'Unknown Execution Type, can only re-trigger Bot/Workflow Executions',
          timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
        })
      }
      await new Promise(resolve => setTimeout(resolve, 1000))
      await this.getData()
      item.retriggerLoader = false
    }
  }
}
</script>

<style scoped>
.horizontal-scroll {
  width: 50em;
  overflow-x: auto;
  white-space: nowrap;
}

.tree-view-item {
  font-family: monospace;
  font-size: 14px;
  margin-left: 18px;
}

.tree-view-wrapper {
  overflow: auto;
}

/* Find the first nested node and override the indentation */
.tree-view-item-root > .tree-view-item-leaf > .tree-view-item {
  margin-left: 0;
}

/* Root node should not be indented */
.tree-view-item-root {
  margin-left: 0;
}

.tree-view-item-node {
  cursor: pointer;
  position: relative;
  white-space: nowrap;
}

.tree-view-item-leaf {
  white-space: nowrap;
}

.tree-view-item-key {
  font-weight: bold;
}

.tree-view-item-key-with-chevron {
  padding-left: 14px;
}

.tree-view-item-key-with-chevron.opened::before {
  top: 4px;
  transform: rotate(90deg);
  -webkit-transform: rotate(90deg);
}

.tree-view-item-key-with-chevron::before {
  color: #444;
  content: '\25b6';
  font-size: 10px;
  left: 1px;
  position: absolute;
  top: 3px;
  transition: -webkit-transform 0.1s ease;
  transition: transform 0.1s ease;
  transition: transform 0.1s ease, -webkit-transform 0.1s ease;
  -webkit-transition: -webkit-transform 0.1s ease;
}

.tree-view-item-hint {
  color: #ccc;
}
</style>
